import { useState } from 'react';
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import { workbookReferences } from '@erp/workbooks';
import type { WorkbookEdits } from './workbookEdits';
import { Button } from '@/components/ui/button';
import type { MentionSource } from '@/components/common/lexical-mentions/types';
import { WorkbookInstructionEditor } from './WorkbookInstructionEditor';

/**
 * The Workbook's goal: what it should do, in the user's words, from which the
 * whole Process is generated. Open while there is no Process yet; afterwards
 * `collapsed` folds it to one line above the steps.
 */
export function WorkbookInstructions({
  definition,
  canUpdate,
  edits,
  mentionSources,
  collapsed = false,
}: {
  definition: WorkbookDefinition;
  canUpdate: boolean;
  edits: WorkbookEdits;
  mentionSources: MentionSource[];
  collapsed?: boolean;
}) {
  const gt = useGT();
  const [editing, setEditing] = useState(!definition.instructions);
  const [open, setOpen] = useState(false);
  const heading = (
    <T context="workbook original prompt heading">
      What should this workbook do?
    </T>
  );
  if (collapsed && !open)
    return (
      <button
        type="button"
        aria-expanded={false}
        onClick={() => setOpen(true)}
        className="flex min-w-0 items-center gap-2 rounded-lg border border-input bg-card px-3 py-1.5 text-left text-sm hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring"
      >
        <CaretRightIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="shrink-0 font-medium">{heading}</span>
        <span className="min-w-0 truncate text-muted-foreground">
          {
            workbookReferences(definition)
              .replace(definition.instructions, (reference) =>
                reference.kind === 'missing' ? '' : reference.name,
              )
              .split('\n')[0]
          }
        </span>
      </button>
    );
  return (
    <div className="space-y-2 rounded-lg border border-input bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">
          {collapsed ? (
            <button
              type="button"
              aria-expanded
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded focus-visible:outline-2 focus-visible:outline-ring"
            >
              <CaretDownIcon className="size-4 text-muted-foreground" />
              {heading}
            </button>
          ) : (
            heading
          )}
        </h3>
        {canUpdate && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(!editing)}
          >
            {editing ? <CheckIcon /> : <PencilSimpleIcon />}
            {editing ? (
              <T context="workbook finish editing instructions">Done</T>
            ) : (
              <T context="workbook edit instructions">Edit instructions</T>
            )}
          </Button>
        )}
      </div>
      <WorkbookInstructionEditor
        value={definition.instructions}
        readOnly={!canUpdate || !editing}
        onChange={(instructions) => {
          if (canUpdate && editing)
            edits.workbook.update((latest) => ({ ...latest, instructions }));
        }}
        mentionSources={mentionSources}
        label={gt('Process instructions', {
          context: 'workbook instructions input',
        })}
      />
      {editing && (
        <p className="text-xs text-muted-foreground">
          <T context="workbook process instruction help">
            Describe the result you want. Type @ to reference data, files, or
            worksheets, then generate the process.
          </T>
        </p>
      )}
    </div>
  );
}
